import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBookings, useExtendBooking } from '../api/queries'
import type { BookingDetails } from '../api/types'
import { BookingDialog } from '../components/BookingDialog'
import { DeclineDialog } from '../components/DeclineDialog'
import { ErrorNotice } from '../components/ErrorNotice'
import { Badge, Button, Empty, Loading, PageTitle, Segmented } from '../components/ui'
import { cn } from '../lib/cn'
import { fmtDate, fmtDuration, fmtRange, minutesBetween, parse } from '../lib/time'
import { participants as participantsWord } from '../lib/words'

/**
 * Экран 2.5 «Встречи».
 *
 * Раздел называется не «Мои брони» намеренно: участников на каждой встрече больше,
 * чем организаторов, и встреча, на которую позвали, — тоже своя. Роль показана
 * пометкой в строке, а фильтр по ней — вторая ось прав, не глобальная роль.
 *
 * Повторяющаяся серия приходит одной строкой, а не двадцатью: это следствие того,
 * что серия хранится правилом.
 */

type RoleFilter = 'any' | 'organizer' | 'participant'

export function MeetingsPage() {
  const [role, setRole] = useState<RoleFilter>('any')
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming')
  const [openId, setOpenId] = useState<number | null>(null)

  const { data, isLoading, error, refetch } = useBookings(scope, role)

  return (
    <>
      <PageTitle
        action={
          <Link to="/search">
            <Button variant="primary">Забронировать</Button>
          </Link>
        }
      >
        Встречи
      </PageTitle>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Segmented
          label="Моя роль на встрече"
          value={role}
          onChange={setRole}
          options={[
            { value: 'any', label: 'Все' },
            { value: 'organizer', label: 'Я организую' },
            { value: 'participant', label: 'Меня позвали' },
          ]}
        />
        <Segmented
          label="Когда"
          value={scope}
          onChange={setScope}
          options={[
            { value: 'upcoming', label: 'Впереди' },
            { value: 'past', label: 'Прошедшие' },
          ]}
        />
      </div>

      {error && <ErrorNotice error={error} onRetry={() => void refetch()} className="mb-4" />}
      {isLoading && <Loading />}

      {data && data.length === 0 && (
        <Empty
          title={emptyTitle(role, scope)}
          hint={
            scope === 'upcoming'
              ? 'Забронируйте переговорную — встреча появится здесь, а участники получат письмо.'
              : 'Здесь будут встречи, которые уже прошли.'
          }
          action={
            scope === 'upcoming' ? (
              <Link to="/search">
                <Button variant="primary">Забронировать</Button>
              </Link>
            ) : undefined
          }
        />
      )}

      <div className="flex flex-col gap-2">
        {(data ?? []).map((booking) => (
          <MeetingRow key={booking.id} booking={booking} onOpen={() => setOpenId(booking.id)} />
        ))}
      </div>

      {openId !== null && <BookingDialog bookingId={openId} onClose={() => setOpenId(null)} />}
    </>
  )
}

function MeetingRow({ booking, onOpen }: { booking: BookingDetails; onOpen: () => void }) {
  const extend = useExtendBooking()
  const [declining, setDeclining] = useState(false)

  const now = new Date()
  const ongoing = parse(booking.starts_at) <= now && parse(booking.ends_at) > now
  const role = booking.my_role ?? 'outsider'

  return (
    <div
      className={cn(
        'card flex flex-wrap items-center gap-3 px-4 py-3',
        ongoing && 'border-accent/50',
      )}
    >
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 text-left">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{booking.title || 'Занято'}</span>
          {role === 'organizer' ? (
            <Badge tone="accent">Я организую</Badge>
          ) : role === 'participant' ? (
            <Badge>Меня позвали</Badge>
          ) : null}
          {booking.recurrence && (
            <Badge tone="accent">{booking.recurrence.human_readable ?? 'Серия'}</Badge>
          )}
          {ongoing && <Badge tone="soon">Идёт сейчас</Badge>}
        </div>
        <p className="tnum mt-0.5 text-sm text-muted">
          {booking.room?.name ?? booking.room_name ?? `Комната #${booking.room_id}`} ·{' '}
          {fmtDate(booking.starts_at)},{' '}
          {fmtRange(booking.starts_at, booking.ends_at)} ·{' '}
          {fmtDuration(minutesBetween(booking.starts_at, booking.ends_at))}
          {booking.participants && booking.participants.length > 0 && (
            <> · {participantsWord(booking.participants.length)}</>
          )}
        </p>
      </button>

      <div className="flex flex-wrap items-center gap-2">
        {/* Экран 2.13: продление идущей встречи. Кнопка активна, только если следующий
            слот свободен — это решает сервер, а не интерфейс. */}
        {ongoing && booking.can_extend && (
          <Button
            size="sm"
            variant="primary"
            loading={extend.isPending}
            onClick={() => extend.mutate({ id: booking.id, minutes: 30 })}
          >
            + 30 минут
          </Button>
        )}

        {/* Экран 2.12: действие участника. Отменить встречу целиком он не может.
            Спрашиваем подтверждение: кнопка стоит вплотную к безобидному «Открыть»,
            а промах по ней необратим. */}
        {role === 'participant' && (
          <Button size="sm" variant="ghost" onClick={() => setDeclining(true)}>
            Не смогу прийти
          </Button>
        )}

        <Button size="sm" variant="ghost" onClick={onOpen}>
          Открыть
        </Button>
      </div>

      {extend.error && (
        <div className="w-full">
          <ErrorNotice error={extend.error} />
        </div>
      )}

      {declining && <DeclineDialog booking={booking} onClose={() => setDeclining(false)} />}
    </div>
  )
}

function emptyTitle(role: RoleFilter, scope: 'upcoming' | 'past'): string {
  if (scope === 'past') return 'Прошедших встреч нет'
  if (role === 'organizer') return 'Вы пока ничего не бронировали'
  if (role === 'participant') return 'Вас пока никуда не позвали'
  return 'Впереди встреч нет'
}
